import React, { useId } from 'react';
import type { MotionElement, Paint } from '@/lib/motion';

function polygonPoints(element: MotionElement): string {
  const width = element.width ?? 100;
  const height = element.height ?? 100;
  const sides = element.sides ?? 3;
  const star = element.innerRadius !== undefined;
  const count = star ? sides * 2 : sides;
  return Array.from({ length: count }, (_, i) => {
    const angle = -Math.PI / 2 + i * Math.PI * 2 / count;
    const radius = star && i % 2 ? element.innerRadius! : 1;
    return `${width / 2 + Math.cos(angle) * width / 2 * radius},${height / 2 + Math.sin(angle) * height / 2 * radius}`;
  }).join(' ');
}

/** Shared geometry lets group masks use the exact same animated shape as the visible child. */
export function MotionShapeGeometry({ element, fill, mask = false }: { element: MotionElement; fill?: string; mask?: boolean }) {
  const width = element.width ?? 100;
  const height = element.height ?? 100;
  const start = Math.max(0, Math.min(1, element.trimStart ?? 0));
  const end = Math.max(start, Math.min(1, element.trimEnd ?? 1));
  const common = {
    fill: mask ? '#ffffff' : fill ?? 'none',
    stroke: mask ? undefined : element.stroke,
    strokeWidth: mask ? undefined : element.strokeWidth,
  };
  if (element.type === 'rect') return <rect {...common} width={width} height={height} rx={element.radius} />;
  if (element.type === 'ellipse') return <ellipse {...common} cx={width / 2} cy={height / 2} rx={width / 2} ry={height / 2} />;
  if (element.type === 'polygon') return <polygon {...common} points={polygonPoints(element)} />;
  return <path {...common} d={element.d} strokeLinecap={element.lineCap} strokeLinejoin={element.lineJoin}
    pathLength={1} strokeDasharray={mask || (start === 0 && end === 1) ? undefined : `${end - start} 1`}
    strokeDashoffset={mask || (start === 0 && end === 1) ? undefined : -(start + (element.trimOffset ?? 0))} />;
}

function Gradient({ id, paint }: { id: string; paint: Exclude<Paint, string> }) {
  const stops = paint.stops.map((stop, index) => <stop key={index} offset={stop.offset} stopColor={stop.color} />);
  if (paint.type === 'radial') return <radialGradient id={id}>{stops}</radialGradient>;
  const angle = ((paint.angle ?? 0) - 90) * Math.PI / 180;
  const dx = Math.cos(angle) / 2;
  const dy = Math.sin(angle) / 2;
  return <linearGradient id={id} x1={`${(0.5 - dx) * 100}%`} y1={`${(0.5 - dy) * 100}%`} x2={`${(0.5 + dx) * 100}%`} y2={`${(0.5 + dy) * 100}%`}>{stops}</linearGradient>;
}

export function MotionShape({ element, canvasWidth, canvasHeight }: { element: MotionElement; canvasWidth: number; canvasHeight: number }) {
  const id = `motion-gradient-${useId().replace(/:/g, '')}`;
  const gradient = typeof element.fill === 'object' ? element.fill : null;
  return <svg width={element.width ?? canvasWidth} height={element.height ?? canvasHeight} style={{ display: 'block', overflow: 'visible' }}>
    {gradient && <defs><Gradient id={id} paint={gradient} /></defs>}
    <MotionShapeGeometry element={element} fill={gradient ? `url(#${id})` : typeof element.fill === 'string' ? element.fill : undefined} />
  </svg>;
}
